import { useEffect, useState, useMemo, useCallback } from 'react';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import FileViewerModal from '../../components/workspace/FileViewerModal';
import ExpirationField from '../../components/workspace/ExpirationField';
import RetryClassificationButton from '../../components/workspace/RetryClassificationButton';
import Btn from '../../components/ui/Btn';
import { Card, Badge, Avatar, Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { useToast } from '../../lib/toast';
import { useIsMobile } from '../../lib/useBreakpoint';

// ────────────────────────────────────────────────────────────────────────────
const STATUS_INFO = {
  in_review:   { variant: 'warn',   label: 'In review' },
  approved:    { variant: 'ok',     label: 'Approved' },
  rejected:    { variant: 'danger', label: 'Rejected' },
  classifying: { variant: 'info',   label: 'Classifying…' },
  unmatched:   { variant: 'danger', label: 'Unmatched' },
};

const TABS = [
  { k: 'in_review', label: 'Pending review' },
  { k: 'all',       label: 'All documents' },
  { k: 'approved',  label: 'Approved' },
  { k: 'rejected',  label: 'Rejected' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return Math.floor(diff / 60) + 'm';
  if (diff < 86400)     return Math.floor(diff / 3600) + 'h';
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + 'd';
  return new Date(iso).toLocaleDateString();
}

const ICON_BTN_STYLE = {
  width: 28, height: 28, border: 'none', background: 'transparent',
  borderRadius: 6, color: 'var(--text-3)', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

// ────────────────────────────────────────────────────────────────────────────
export default function DocumentsPanel() {
  const { team, isStaff, refreshCounts } = useWorkspace();
  const confirm = useConfirm();
  const toast = useToast();
  const isMobile = useIsMobile();

  const [tab, setTab]           = useState('in_review');
  const [search, setSearch]     = useState('');
  const [docs, setDocs]         = useState([]);
  const [members, setMembers]   = useState({}); // memberId -> {name, email, color}
  const [templates, setTemplates] = useState({}); // templateId -> full template object
  const [templatesArr, setTemplatesArr] = useState([]); // for the file viewer reassign dropdown
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [actingId, setActingId] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);

  const loadDocs = useCallback(async () => {
    if (!team?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [uploadsRes, membersRes, templatesRes] = await Promise.all([
        supabase
          .from('document_uploads')
          .select('*')
          .eq('team_id', team.id)
          .order('created_at', { ascending: false })
          .limit(500),
        supabase
          .from('team_members')
          .select('id, email, first_name, last_name')
          .eq('team_id', team.id),
        supabase
          .from('document_templates')
          .select('id, label, has_expiration')
          .eq('team_id', team.id),
      ]);
      if (uploadsRes.error) throw uploadsRes.error;

      setDocs(uploadsRes.data || []);

      const memMap = {};
      (membersRes.data || []).forEach(m => {
        const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
        memMap[m.id] = { name, email: m.email };
      });
      setMembers(memMap);

      const tplMap = {};
      const tplArr = templatesRes.data || [];
      tplArr.forEach(t => { tplMap[t.id] = t; });
      setTemplates(tplMap);
      setTemplatesArr(tplArr);
    } catch (err) {
      console.error('Documents load failed:', err);
      setError(err.message || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [team?.id]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // ── Counts per tab ────────────────────────────────────────────────────
  const tabCounts = useMemo(() => {
    return {
      in_review: docs.filter(d => d.status === 'in_review').length,
      approved:  docs.filter(d => d.status === 'approved').length,
      rejected:  docs.filter(d => d.status === 'rejected').length,
      all:       docs.length,
    };
  }, [docs]);

  // ── Filtered + searched view ──────────────────────────────────────────
  const visibleDocs = useMemo(() => {
    let xs = docs;
    if (tab !== 'all') xs = xs.filter(d => d.status === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(d => {
        const name = (d.original_filename || '').toLowerCase();
        const m = members[d.member_id];
        const memberName = (m?.name || '').toLowerCase();
        return name.includes(q) || memberName.includes(q);
      });
    }
    return xs;
  }, [docs, tab, search, members]);

  // ── Approve action ────────────────────────────────────────────────────
  const approve = async (doc) => {
    if (actingId) return;
    setActingId(doc.id);
    try {
      const { error: updErr } = await supabase
        .from('document_uploads')
        .update({ status: 'approved', reviewed_at: new Date().toISOString() })
        .eq('id', doc.id);
      if (updErr) throw updErr;
      // Optimistic local update
      setDocs(docs.map(d => d.id === doc.id ? { ...d, status: 'approved' } : d));
      refreshCounts();
    } catch (err) {
      console.error('Approve failed:', err);
      toast.error('Could not approve: ' + (err.message || 'unknown error'));
    } finally {
      setActingId(null);
    }
  };

  // ── Reject action — prompts for reason, fires notification ───────────
  const reject = async (doc) => {
    if (actingId) return;
    const r = await confirm({
      title: 'Reject document',
      message: 'Tell the uploader why so they can resubmit. They\'ll be notified by email (and SMS if opted in).',
      inputLabel: 'Reason',
      inputPlaceholder: 'e.g. The expiration date isn\'t legible — please re-scan.',
      inputRequired: true,
      confirmLabel: 'Reject',
      confirmVariant: 'danger',
    });
    if (!r.confirmed) return;
    const reason = r.input;

    setActingId(doc.id);
    try {
      const { error: updErr } = await supabase
        .from('document_uploads')
        .update({
          status: 'rejected',
          admin_notes: reason || 'No reason provided',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', doc.id);
      if (updErr) throw updErr;

      setDocs(docs.map(d => d.id === doc.id
        ? { ...d, status: 'rejected', admin_notes: reason || 'No reason provided' }
        : d
      ));
      refreshCounts();

      // Fire notification (best-effort, non-blocking — don't fail the whole flow).
      // Backend expects { upload_id, new_status, team_id, member_id, admin_notes }.
      try {
        await api('/api/send-notification', {
          upload_id: doc.id,
          new_status: 'rejected',
          team_id: team.id,
          member_id: doc.member_id,
          admin_notes: reason || 'No reason provided',
        });
      } catch (notifyErr) {
        console.warn('Rejection notification failed (non-fatal):', notifyErr);
      }
    } catch (err) {
      console.error('Reject failed:', err);
      toast.error('Could not reject: ' + (err.message || 'unknown error'));
    } finally {
      setActingId(null);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title="Review queue"
        subtitle="All documents uploaded by team members — review, approve, or reject with notes."
        actions={
          <Btn variant="outline" size="md" icon={Icon.refresh} onClick={loadDocs}>
            Refresh
          </Btn>
        }
      />

      {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {/* Tabs + search toolbar */}
        <div style={{
          padding: isMobile ? '10px 12px' : '12px 16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 10 : 10,
          borderBottom: '1px solid var(--border)',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
        }}>
          {/* Tabs — horizontally scrollable on mobile when too wide */}
          <div style={{
            display: 'flex', background: 'var(--surface-2)',
            borderRadius: 8, padding: 2,
            overflowX: isMobile ? 'auto' : 'visible',
            maxWidth: '100%',
            scrollbarWidth: 'none',
          }}>
            {TABS.map((t) => {
              const active = tab === t.k;
              const count = tabCounts[t.k] || 0;
              const isAlert = t.k === 'in_review' && count > 0;
              return (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTab(t.k)}
                  style={{
                    padding: isMobile ? '7px 12px' : '5px 11px',
                    fontSize: 12.5, fontWeight: 600,
                    border: 'none',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--text)' : 'var(--text-3)',
                    borderRadius: 6, cursor: 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 6,
                    boxShadow: active ? 'var(--shadow-xs)' : 'none',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.label}
                  <span style={{
                    fontSize: 10, padding: '1px 5px',
                    background: isAlert && !active ? 'var(--warn-bg)' : 'var(--surface-3)',
                    color: isAlert ? 'var(--warn)' : 'var(--text-3)',
                    borderRadius: 99, fontWeight: 700,
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {!isMobile && <div style={{ flex: 1 }} />}

          <div style={{ width: isMobile ? '100%' : 280 }}>
            <Input
              icon={Icon.search}
              placeholder="Search by filename or uploader…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={isMobile ? undefined : { height: 32, fontSize: 12.5 }}
            />
          </div>
        </div>

        {/* Body */}
        {loading && docs.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Spinner size={20} color="var(--accent)" />
            <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-3)' }}>Loading documents…</div>
          </div>
        ) : visibleDocs.length === 0 ? (
          <EmptyState tab={tab} hasAny={docs.length > 0} />
        ) : isMobile ? (
          <DocumentCardList
            docs={visibleDocs}
            members={members}
            templates={templates}
            isStaff={isStaff}
            actingId={actingId}
            onApprove={approve}
            onReject={reject}
            onView={(d) => setViewingDoc(d)}
            onUpdate={(updated) => setDocs(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d))}
          />
        ) : (
          <DocumentTable
            docs={visibleDocs}
            members={members}
            templates={templates}
            isStaff={isStaff}
            actingId={actingId}
            onApprove={approve}
            onReject={reject}
            onView={(d) => setViewingDoc(d)}
            onUpdate={(updated) => setDocs(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d))}
          />
        )}

        {/* Footer */}
        <div style={{
          padding: '10px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', fontSize: 12, color: 'var(--text-3)',
          borderTop: '1px solid var(--border)',
        }}>
          <span>
            Showing {visibleDocs.length} of {docs.length}
          </span>
        </div>
      </Card>

      {viewingDoc && (
        <FileViewerModal
          doc={viewingDoc}
          templates={templatesArr}
          canReview={isStaff}
          onClose={() => setViewingDoc(null)}
          onChanged={(updated) => {
            setDocs(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
            setViewingDoc(updated);
            refreshCounts?.();
          }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────────────────────────
function DocumentTable({ docs, members, templates, isStaff, actingId, onApprove, onReject, onView, onUpdate }) {
  const { teamId } = useWorkspace();
  return (
    <div>
      {/* Header row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 2fr) 1fr 1fr 110px 180px',
        gap: 12, padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)',
        fontSize: 11, fontWeight: 600, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        <div>Document</div>
        <div>Uploaded by</div>
        <div>Template</div>
        <div>Status</div>
        <div style={{ textAlign: 'right' }}>Actions</div>
      </div>

      {docs.map((d) => {
        const member = members[d.member_id];
        const memberName = member?.name || '(unknown)';
        const tplLabel = d.template_id ? (templates[d.template_id]?.label || '(deleted template)') : (d.display_name || d.classified_as || 'Unmatched');
        const status = STATUS_INFO[d.status] || STATUS_INFO.in_review;
        const isActing = actingId === d.id;

        return (
          <div
            key={d.id}
            onClick={() => onView(d)}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(280px, 2fr) 1fr 1fr 110px 180px',
              gap: 12, padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {/* Document */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <ThumbIcon filename={d.original_filename} />
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {d.template_id ? (templates[d.template_id]?.label || 'Deleted template') : (d.display_name || 'Unclassified')}
                </div>
                <div style={{
                  fontSize: 11, color: 'var(--text-3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  marginTop: 1,
                }}>
                  {d.original_filename || '(unnamed)'}
                </div>
                {d.admin_notes && d.status === 'rejected' && (
                  <div style={{
                    fontSize: 11, color: 'var(--danger)',
                    marginTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    Rejected: {d.admin_notes}
                  </div>
                )}
              </div>
            </div>

            {/* Member */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <Avatar name={memberName} size={24} />
              <span style={{
                fontSize: 12.5, color: 'var(--text-2)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {memberName}
              </span>
            </div>

            {/* Template */}
            <div style={{ fontSize: 12, color: 'var(--text-2)', minWidth: 0 }}>
              {d.template_id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon.sparkle size={11} stroke="var(--accent-600)" />
                  <span style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {tplLabel}
                  </span>
                </div>
              ) : (
                <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>
                  {tplLabel}
                </span>
              )}
              {/* Inline expiration display — only if template tracks it */}
              {d.template_id && templates[d.template_id]?.has_expiration && (
                <div style={{ marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                  <ExpirationField
                    uploadId={d.id}
                    expiresAt={d.expires_at}
                    canEdit={isStaff}
                    onChanged={(newDate) => onUpdate?.({ id: d.id, expires_at: newDate })}
                    variant="compact"
                  />
                </div>
              )}
            </div>

            {/* Status */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <Badge variant={status.variant} dot>{status.label}</Badge>
              {d.classification_status === 'classification_failed' && (
                <Badge variant="warn" size="xs">
                  <Icon.alert size={9} /> Class. failed
                </Badge>
              )}
            </div>

            {/* Actions */}
            <div
              style={{
                display: 'flex', gap: 4, justifyContent: 'flex-end',
                alignItems: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {d.classification_status === 'classification_failed' && (
                <RetryClassificationButton
                  doc={d}
                  teamId={teamId}
                  onUpdated={(updated) => onUpdate?.(updated)}
                  variant="button-only"
                />
              )}
              {isStaff && d.status === 'in_review' && (
                <button
                  type="button"
                  style={{ ...ICON_BTN_STYLE, color: 'var(--ok)', background: 'var(--ok-bg)' }}
                  onClick={() => onApprove(d)}
                  disabled={isActing}
                  title="Approve"
                >
                  {isActing ? <Spinner size={12} color="var(--ok)" /> : <Icon.check size={14} />}
                </button>
              )}
              {isStaff && d.status === 'in_review' && (
                <button
                  type="button"
                  style={{ ...ICON_BTN_STYLE, color: 'var(--danger)', background: 'var(--danger-bg)' }}
                  onClick={() => onReject(d)}
                  disabled={isActing}
                  title="Reject"
                >
                  {isActing ? <Spinner size={12} color="var(--danger)" /> : <Icon.x size={14} />}
                </button>
              )}
              <Btn
                variant="outline"
                size="sm"
                icon={Icon.eye}
                onClick={() => onView(d)}
              >
                View
              </Btn>
              {!isStaff && (
                <span style={{
                  fontSize: 11, color: 'var(--text-3)',
                  fontVariantNumeric: 'tabular-nums', marginLeft: 4,
                }}>
                  {timeAgo(d.created_at)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile card layout — replaces the 5-col grid table on small viewports.
// Each card surfaces: thumb + title + status badge / filename / rejection
// note / expiration / submitter line / action button row.
// ────────────────────────────────────────────────────────────────────────────
function DocumentCardList({ docs, members, templates, isStaff, actingId, onApprove, onReject, onView, onUpdate }) {
  const { teamId } = useWorkspace();
  return (
    <div>
      {docs.map((d) => {
        const member = members[d.member_id];
        const memberName = member?.name || '(unknown)';
        const tpl = d.template_id ? templates[d.template_id] : null;
        const title = tpl?.label || d.display_name || (d.template_id ? 'Deleted template' : 'Unclassified');
        const status = STATUS_INFO[d.status] || STATUS_INFO.in_review;
        const isActing = actingId === d.id;
        const hasExpiration = !!(tpl?.has_expiration);

        return (
          <div
            key={d.id}
            onClick={() => onView(d)}
            style={{
              padding: '12px',
              borderBottom: '1px solid var(--border)',
              cursor: 'pointer',
              background: 'var(--surface)',
            }}
          >
            {/* Top row: thumb + title + status badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <ThumbIcon filename={d.original_filename} compact />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {title}
                </div>
                <div style={{
                  fontSize: 11, color: 'var(--text-3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  marginTop: 1,
                }}>
                  {d.original_filename || '(unnamed)'}
                </div>
              </div>
              <Badge variant={status.variant} size="xs" dot>{status.label}</Badge>
            </div>

            {/* Rejection note */}
            {d.admin_notes && d.status === 'rejected' && (
              <div style={{
                fontSize: 11.5, color: 'var(--danger)',
                background: 'var(--danger-bg)',
                padding: '5px 9px', borderRadius: 6,
                marginBottom: 6, lineHeight: 1.4,
              }}>
                <strong>Rejected:</strong> {d.admin_notes}
              </div>
            )}

            {/* Classification failed — only when explicit status set */}
            {d.classification_status === 'classification_failed' && (
              <div style={{ marginBottom: 6 }} onClick={(e) => e.stopPropagation()}>
                <RetryClassificationButton
                  doc={d}
                  teamId={teamId}
                  onUpdated={(updated) => onUpdate?.(updated)}
                />
              </div>
            )}

            {/* Expiration field (own row when applicable) */}
            {hasExpiration && (
              <div style={{ marginBottom: 6 }} onClick={(e) => e.stopPropagation()}>
                <ExpirationField
                  uploadId={d.id}
                  expiresAt={d.expires_at}
                  canEdit={isStaff}
                  onChanged={(newDate) => onUpdate?.({ id: d.id, expires_at: newDate })}
                  variant="compact"
                />
              </div>
            )}

            {/* Submitter line */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10,
            }}>
              <Avatar name={memberName} size={18} />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                by {memberName} · {timeAgo(d.created_at)}
              </span>
            </div>

            {/* Action row */}
            <div
              style={{ display: 'flex', gap: 6 }}
              onClick={(e) => e.stopPropagation()}
            >
              {isStaff && d.status === 'in_review' && (
                <Btn
                  variant="primary"
                  size="sm"
                  icon={isActing ? null : Icon.check}
                  onClick={() => onApprove(d)}
                  disabled={isActing}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  {isActing ? <Spinner size={12} color="rgba(255,255,255,0.6)" /> : 'Approve'}
                </Btn>
              )}
              {isStaff && d.status === 'in_review' && (
                <Btn
                  variant="danger"
                  size="sm"
                  icon={isActing ? null : Icon.x}
                  onClick={() => onReject(d)}
                  disabled={isActing}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  {isActing ? <Spinner size={12} color="var(--danger)" /> : 'Reject'}
                </Btn>
              )}
              <Btn
                variant="outline"
                size="sm"
                icon={Icon.eye}
                onClick={() => onView(d)}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                View
              </Btn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function EmptyState({ tab, hasAny }) {
  return (
    <div style={{
      padding: '60px 24px', textAlign: 'center', color: 'var(--text-3)',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
        background: 'var(--surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon.inbox size={26} stroke="var(--text-muted)" />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        {hasAny
          ? `No ${tab.replace('_', ' ')} documents`
          : 'No documents uploaded yet'}
      </div>
      <div style={{ fontSize: 12 }}>
        {hasAny
          ? 'Try a different tab or clear your search.'
          : 'Documents uploaded by team members will appear here.'}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function ThumbIcon({ filename, compact }) {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext);
  const size = compact ? 30 : 36;
  if (isImage) {
    return (
      <div style={{
        width: size, height: size, borderRadius: compact ? 7 : 8,
        background: 'linear-gradient(135deg, #67d97f, #28a745)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', flexShrink: 0,
      }}>
        <Icon.image size={compact ? 13 : 15} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: compact ? 7 : 8,
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#ef4444', flexShrink: 0, fontWeight: 700,
      fontSize: compact ? 9 : 10,
      letterSpacing: '0.04em', fontFamily: 'var(--font-mono)',
      textTransform: 'uppercase',
    }}>
      {ext || 'FILE'}
    </div>
  );
}
